import { useState } from 'react';
import { Send, Plus, X, Copy, Globe } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea, Field } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Badge } from '@/components/ui/Badge';
import { copyToClipboard, formatBytes } from '@/lib/utils';
import { toast } from 'sonner';

const METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'];

interface Header {
  key: string;
  value: string;
}

interface Response {
  status: number;
  statusText: string;
  ok: boolean;
  headers: [string, string][];
  body: string;
  bodyType: 'json' | 'text' | 'html';
  ms: number;
  size: number;
}

export function ApiTester() {
  const [method, setMethod] = useState('GET');
  const [url, setUrl] = useState('https://jsonplaceholder.typicode.com/users/1');
  const [headers, setHeaders] = useState<Header[]>([
    { key: 'Content-Type', value: 'application/json' },
  ]);
  const [body, setBody] = useState('');
  const [response, setResponse] = useState<Response | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function send() {
    setLoading(true);
    setError('');
    setResponse(null);
    const start = performance.now();
    try {
      const headerObj: Record<string, string> = {};
      headers.forEach((h) => {
        if (h.key.trim()) headerObj[h.key] = h.value;
      });
      const init: RequestInit = {
        method,
        headers: headerObj,
      };
      if (method !== 'GET' && method !== 'HEAD' && body) {
        init.body = body;
      }
      const r = await fetch(url, init);
      const ms = performance.now() - start;
      const text = await r.text();
      const ct = r.headers.get('content-type') ?? '';
      let bodyType: Response['bodyType'] = 'text';
      let body2 = text;
      if (ct.includes('json')) {
        bodyType = 'json';
        try {
          body2 = JSON.stringify(JSON.parse(text), null, 2);
        } catch {
          /* keep as is */
        }
      } else if (ct.includes('html')) {
        bodyType = 'html';
      }
      setResponse({
        status: r.status,
        statusText: r.statusText,
        ok: r.ok,
        headers: Array.from(r.headers.entries()),
        body: body2,
        bodyType,
        ms: Math.round(ms),
        size: text.length,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  function curl(): string {
    const parts = [`curl -X ${method}`];
    headers.forEach((h) => {
      if (h.key.trim()) parts.push(`-H ${JSON.stringify(`${h.key}: ${h.value}`)}`);
    });
    if (body && method !== 'GET' && method !== 'HEAD') {
      parts.push(`-d ${JSON.stringify(body)}`);
    }
    parts.push(JSON.stringify(url));
    return parts.join(' \\\n  ');
  }

  return (
    <div>
      <ToolHeader
        toolId="api-tester"
        title="API request tester"
        description="Postman-lite. Compose a request, view response, copy as cURL."
      />

      <Card className="mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr_auto] gap-2">
          <Select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            options={METHODS.map((m) => ({ value: m, label: m }))}
          />
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://api.example.com/users"
            onKeyDown={(e) => e.key === 'Enter' && send()}
            className="font-mono"
          />
          <Button variant="primary" onClick={send} loading={loading}>
            <Send size={12} />
            Send
          </Button>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="!p-0">
          <Tabs defaultValue="headers">
            <div className="px-3 py-2 border-b border-border">
              <TabsList>
                <TabsTrigger value="headers">Headers</TabsTrigger>
                <TabsTrigger value="body">Body</TabsTrigger>
                <TabsTrigger value="curl">cURL</TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="headers" className="!mt-0">
              <div className="p-4 space-y-2">
                {headers.map((h, i) => (
                  <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                    <Input
                      value={h.key}
                      onChange={(e) =>
                        setHeaders((arr) =>
                          arr.map((x, idx) => (idx === i ? { ...x, key: e.target.value } : x)),
                        )
                      }
                      placeholder="Header"
                      className="font-mono"
                    />
                    <Input
                      value={h.value}
                      onChange={(e) =>
                        setHeaders((arr) =>
                          arr.map((x, idx) => (idx === i ? { ...x, value: e.target.value } : x)),
                        )
                      }
                      placeholder="Value"
                      className="font-mono"
                    />
                    <Button
                      size="icon-sm"
                      variant="ghost"
                      onClick={() => setHeaders((arr) => arr.filter((_, idx) => idx !== i))}
                    >
                      <X size={11} />
                    </Button>
                  </div>
                ))}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setHeaders((arr) => [...arr, { key: '', value: '' }])}
                >
                  <Plus size={11} />
                  Add header
                </Button>
              </div>
            </TabsContent>
            <TabsContent value="body" className="!mt-0">
              <Textarea
                mono
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder='{"key": "value"}'
                className="!border-0 min-h-[260px] !rounded-none focus-visible:!ring-0"
                disabled={method === 'GET' || method === 'HEAD'}
              />
            </TabsContent>
            <TabsContent value="curl" className="!mt-0">
              <div className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xs uppercase tracking-wider text-fg-subtle">cURL command</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const ok = await copyToClipboard(curl());
                      if (ok) toast.success('Copied');
                    }}
                  >
                    <Copy size={11} />
                  </Button>
                </div>
                <pre className="font-mono text-[12px] leading-relaxed p-3 rounded-lg bg-bg-inset border border-border overflow-auto">
                  <code>{curl()}</code>
                </pre>
              </div>
            </TabsContent>
          </Tabs>
        </Card>

        <Card className="!p-0">
          <div className="px-4 py-2.5 border-b border-border flex items-center justify-between">
            <span className="text-xs font-medium text-fg-muted">Response</span>
            {response && (
              <div className="flex items-center gap-2">
                <Badge variant={response.ok ? 'success' : 'danger'}>
                  {response.status} {response.statusText}
                </Badge>
                <span className="text-2xs font-mono text-fg-subtle">{response.ms} ms</span>
                <span className="text-2xs font-mono text-fg-subtle">{formatBytes(response.size)}</span>
              </div>
            )}
          </div>
          {error && (
            <div className="p-4 text-sm text-danger">⚠ {error}</div>
          )}
          {!response && !error && !loading && (
            <div className="p-12 text-center text-sm text-fg-subtle">
              <Globe size={28} className="mx-auto mb-2 text-fg-subtle/50" />
              Send a request to see the response.
            </div>
          )}
          {response && (
            <Tabs defaultValue="body" className="!mt-0">
              <div className="px-3 py-2 border-b border-border">
                <TabsList>
                  <TabsTrigger value="body">Body</TabsTrigger>
                  <TabsTrigger value="headers">
                    Headers <span className="ml-1 text-2xs text-fg-subtle">{response.headers.length}</span>
                  </TabsTrigger>
                </TabsList>
              </div>
              <TabsContent value="body" className="!mt-0">
                <pre className="font-mono text-[12px] leading-relaxed p-4 overflow-auto max-h-[420px] whitespace-pre-wrap break-all">
                  <code>{response.body}</code>
                </pre>
              </TabsContent>
              <TabsContent value="headers" className="!mt-0">
                <div className="divide-y divide-border max-h-[420px] overflow-auto">
                  {response.headers.map(([k, v]) => (
                    <div key={k} className="flex items-start gap-3 px-4 py-2 text-xs">
                      <code className="font-mono font-semibold text-fg-muted w-44 shrink-0 truncate">
                        {k}
                      </code>
                      <code className="font-mono break-all">{v}</code>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </Card>
      </div>
    </div>
  );
}

export default ApiTester;
